// Quiet-hours helpers: wall-clock time in an IANA zone with Intl only. Browser-safe, no dependencies.

export interface LocalParts {
  year: number;
  month: number; // 1-12
  day: number; // 1-31
  hour: number; // 0-23
  minute: number;
  second: number;
  /** 0 = Sunday ... 6 = Saturday, like Date#getDay() */
  weekday: number;
}

export interface WallClock {
  year: number;
  month: number; // 1-12
  day: number;
  hour: number;
  minute: number;
}

const WEEKDAYS: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

const formatters = new Map<string, Intl.DateTimeFormat>();

function formatterFor(tz: string): Intl.DateTimeFormat {
  let f = formatters.get(tz);
  if (!f) {
    // throws RangeError for an unknown zone, which is what we want
    f = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      weekday: 'short',
    });
    formatters.set(tz, f);
  }
  return f;
}

/** The local wall-clock parts of `date` in zone `tz`. */
export function localParts(date: Date, tz: string): LocalParts {
  const out: LocalParts = { year: 0, month: 0, day: 0, hour: 0, minute: 0, second: 0, weekday: 0 };
  for (const p of formatterFor(tz).formatToParts(date)) {
    switch (p.type) {
      case 'year': out.year = Number(p.value); break;
      case 'month': out.month = Number(p.value); break;
      case 'day': out.day = Number(p.value); break;
      case 'hour': out.hour = Number(p.value) % 24; break; // belt and braces against a "24"
      case 'minute': out.minute = Number(p.value); break;
      case 'second': out.second = Number(p.value); break;
      case 'weekday': out.weekday = WEEKDAYS[p.value] ?? 0; break;
      default: break;
    }
  }
  return out;
}

/** Zone offset (local minus UTC) in ms at the UTC instant `utcMs`. */
function offsetMsAt(utcMs: number, tz: string): number {
  const p = localParts(new Date(utcMs), tz);
  const localAsUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  return localAsUtc - Math.floor(utcMs / 1000) * 1000;
}

/**
 * The UTC instant for a wall-clock time in `tz`, found by offset probing: guess UTC, measure
 * the zone offset at the guess, correct, re-measure once. Correct on both sides of a DST
 * change. A wall time inside a spring-forward gap does not exist; it is pushed forward by the
 * gap length (02:30 becomes 03:30). An ambiguous fall-back time resolves to its first
 * (pre-transition) occurrence.
 */
export function zonedTimeToUtc(parts: WallClock, tz: string): Date {
  const wall = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, 0, 0);
  const first = wall - offsetMsAt(wall, tz);
  const second = wall - offsetMsAt(first, tz);
  if (first === second) return new Date(second);
  // the two probes disagree: either the second one is right, or we are in a gap
  const check = localParts(new Date(second), tz);
  const roundTrips =
    check.year === parts.year && check.month === parts.month && check.day === parts.day &&
    check.hour === parts.hour && check.minute === parts.minute;
  return new Date(roundTrips ? second : Math.max(first, second));
}

/** `'HH:MM'` -> minutes after midnight. `24:00` is accepted (end of day). */
export function parseHHMM(s: string): number {
  const m = /^(\d{1,2}):(\d{2})$/.exec(s.trim());
  if (!m) throw new RangeError(`bad time "${s}", expected HH:MM`);
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 24 || min > 59 || (h === 24 && min !== 0)) throw new RangeError(`bad time "${s}", expected HH:MM`);
  return h * 60 + min;
}

function windowMinutes(start: string, end: string): [number, number] {
  const s = parseHHMM(start);
  const e = parseHHMM(end);
  if (e <= s) throw new RangeError(`send window end (${end}) must be after start (${start})`);
  return [s, e];
}

/** true when the local wall-clock time of `now` in `tz` is inside `[start, end)`. */
export function isWithinWindow(now: Date, tz: string, start: string, end: string): boolean {
  const [s, e] = windowMinutes(start, end);
  const p = localParts(now, tz);
  const cur = p.hour * 60 + p.minute;
  return cur >= s && cur < e;
}

/**
 * `now` when inside the window, else the next local `start`: today if we are before it,
 * tomorrow if we are at or after `end`.
 */
export function nextWindowOpen(now: Date, tz: string, start: string, end: string): Date {
  const [s, e] = windowMinutes(start, end);
  const p = localParts(now, tz);
  const cur = p.hour * 60 + p.minute;
  if (cur >= s && cur < e) return now;
  const hour = Math.floor(s / 60);
  const minute = s % 60;
  if (cur < s) {
    return zonedTimeToUtc({ year: p.year, month: p.month, day: p.day, hour, minute }, tz);
  }
  const tomorrow = new Date(Date.UTC(p.year, p.month - 1, p.day + 1));
  return zonedTimeToUtc(
    { year: tomorrow.getUTCFullYear(), month: tomorrow.getUTCMonth() + 1, day: tomorrow.getUTCDate(), hour, minute },
    tz,
  );
}
